/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import { flightsClearConfirmation, flightsClearOptionsIds, flightsClearPagination, flightsFilterClear, flightsSetTravelers } from 'action-creators/flights';
import { hotelsFilterClear, hotelsSetTraveler } from 'action-creators/hotels';
import { fetchUserAccount } from 'actions/user';
import Header from 'components/Header';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import AutosSearch from 'containers/AutosSearch';
import FlightsSearch from 'containers/FlightsSearch';
import HotelsSearch from 'containers/HotelsSearch';
import Menu from 'containers/UserMenu';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { GetAllBillingCompanyResponse } from 'models/billingCompany';
import { amplitudelogEvent, ViewTravelSearchScreenEvent } from 'services/amplitudeService';
import { getAllBillingCompanies } from 'services/billingCompanyService';
import { getAllowedTravelers, TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { dateFormatWeekend } from 'utils/generic';
import { goBackToApp } from 'utils/webViewUtils';

import { Screen, Title, Body, BookingType } from './styles';

enum BookingSwitchEnum {
  Flights = 'flights',
  Hotels = 'hotels',
  Autos = 'autos',
}

const Booking = () => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const history = useHistory<{ errorMessage?: string } | null>();
  const dispatch = useDispatch();
  const user = useUserAccount();

  const { travelers: flightTravelers } = useSelector((state: RootState) => state.flights);
  const { travelers: hotelTravelers } = useSelector((state: RootState) => state.hotels);

  const [userLoaded, setUserLoaded] = useState(false);
  const [allowedTravelers, setAllowedTravelers] = useState<TravelArrangerUsersAllowed[]>([]);
  const [isLoadingAllowedTravelers, setLoadingAllowedTravelers] = useState(true);
  const [isShow, setIsShow] = useState(false);
  const [currentSwitch, setCurrentSwitch] = useState(BookingSwitchEnum.Flights);
  const [branchCompanies, setBranchCompanies] = useState<GetAllBillingCompanyResponse[]>([]);
  const [isLoadingBranchCompanies, setIsLoadingBranchCompanies] = useState(false);
  const companyId = useSelector((state: RootState) => state.user.accountResult?.companyId);
  const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration.configuration);
  const toggleState = useSelector((state: RootState) => state.toggle.value);
  const handleBookingTypeChange = (type: BookingSwitchEnum) => {
    setCurrentSwitch(type);

    history.replace(
      `/corporate/booking/${
        type === BookingSwitchEnum.Hotels ? 'hotels' : type === BookingSwitchEnum.Flights ? 'flights' : 'autos'
      }`,
    );
  };
  const openBottomSheet = () => {
    setIsShow(true);
  };

  const closeBottomSheet = () => {
    setIsShow(false);
  };

  useEffect(() => {
    if (user) {
      setUserLoaded(true);
    } else {
      const fetch = async () => {
        //alert('UPDATE fetchUserAccount ON BOOKING');
        await dispatch(fetchUserAccount());
        setUserLoaded(true);
      };
      fetch();
    }
  }, [dispatch, user]);

  const updateFlightSelectedTraveler = useCallback((list: TravelArrangerUsersAllowed[]) => {
    const selecteds = list.filter((traveler) => flightTravelers?.find((f) => f.id === traveler.userId));
    dispatch(flightsSetTravelers(selecteds));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHotelSelectedTraveler = useCallback((list: TravelArrangerUsersAllowed[]) => {
    const selecteds = list.filter((traveler) => hotelTravelers?.find((f) => f.id === traveler.userId));
    dispatch(hotelsSetTraveler(selecteds));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  enum BookingProductType {
    FLIGHTS = 'FLIGHTS',
    HOTELS = 'HOTELS',
    AUTOS = 'CAR_RENTAL',
  }
  const sendAmplitudeEvent = (productType: BookingProductType) => {
    //Amplitude
    const event: ViewTravelSearchScreenEvent = {
      SOURCE: 'TRAVEL_HOME',
      TRAVEL_PRODUCT_TYPE: productType,
    };

    amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_SEARCH_SCREEN, event, user);
  };

  useEffect(() => {
    if (!userLoaded || !user?.isTravelArranger) {
      setLoadingAllowedTravelers(false);
      updateFlightSelectedTraveler([]);
      updateHotelSelectedTraveler([]);
      return;
    }
    if (toggleState && allowedTravelers.length === 0) {
      const fetch = async () => {
        const { data } = await getAllowedTravelers();
        setAllowedTravelers(data);
        updateFlightSelectedTraveler(data);
        updateHotelSelectedTraveler(data);
        setLoadingAllowedTravelers(false);
      };
      fetch();
    } else {
      setLoadingAllowedTravelers(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userLoaded, updateFlightSelectedTraveler, updateHotelSelectedTraveler, toggleState]);

  useEffect(() => {
    setIsLoadingBranchCompanies(true);
    getAllBillingCompanies(companyId ?? '', true).then((res) => {
      setBranchCompanies(res);
      setIsLoadingBranchCompanies(false);
    });
    if (companyConfiguration.menuView) {
      if (!companyConfiguration?.menuView.hideAirFlow) {
        setCurrentSwitch(BookingSwitchEnum.Flights);
      } else if (!companyConfiguration?.menuView.hideHotelFlow) {
        setCurrentSwitch(BookingSwitchEnum.Hotels);
      } else if (!companyConfiguration?.menuView.hideVehicleFlow) {
        setCurrentSwitch(BookingSwitchEnum.Autos);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (history.location.pathname === '/corporate/booking/hotels') {
      setCurrentSwitch(BookingSwitchEnum.Hotels);
      sendAmplitudeEvent(BookingProductType.HOTELS);
      return;
    }
    if (history.location.pathname === '/corporate/booking/autos') {
      setCurrentSwitch(BookingSwitchEnum.Autos);
      sendAmplitudeEvent(BookingProductType.AUTOS);
      return;
    }
    if (history.location.pathname === '/corporate/booking/flights') {
      sendAmplitudeEvent(BookingProductType.FLIGHTS);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  useEffect(() => {
    if (history.location.state && history.location.state.errorMessage) {
      let message = '';
      const messageSplitted = history.location.state.errorMessage.split(' ');
      const formattedDate = dateFormatWeekend(messageSplitted[messageSplitted.length - 1]);
      message = t('flightsSearch.invalidDateError', { date: formattedDate });
      toast.add({ type: 'danger', message: message || t('defaultError.default') });
      history.replace('/booking', null);
    }
  }, [history.location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  useEffect(() => {
    dispatch(flightsFilterClear());
    dispatch(hotelsFilterClear());
    dispatch(flightsClearPagination());
    dispatch(flightsClearConfirmation())
    dispatch(flightsClearOptionsIds());
  }, [dispatch]);

  const allOptions = [
    {
      label: t('booking.flights'),
      value: BookingSwitchEnum.Flights,
      testId: 'booking-flight-tab',
      hide: companyConfiguration?.menuView?.hideAirFlow ?? false,
    },
    {
      label: t('booking.hotels'),
      value: BookingSwitchEnum.Hotels,
      testId: 'booking-hotel-tab',
      hide: companyConfiguration?.menuView?.hideHotelFlow ?? false,
    },
    {
      label: t('booking.autos'),
      value: BookingSwitchEnum.Autos,
      testId: 'booking-autos-tab',
      hide: companyConfiguration?.menuView?.hideVehicleFlow ?? false,
    },
  ];

  const allOptionsFiltered = allOptions.filter((item) => item.hide !== true);

  return (
    <Screen>
      <Mobile>
        <Header openMenu={openBottomSheet} goBack={goBackToApp} />
      </Mobile>

      <Desktop>
        <ScreenHeader title={t('booking.title')} />
      </Desktop>

      <Body>
        <Mobile>
          <Title>{t('booking.title')}</Title>
        </Mobile>

        <BookingType
          className={currentSwitch}
          options={allOptionsFiltered}
          active={currentSwitch}
          onChange={handleBookingTypeChange}
        />
        {currentSwitch === BookingSwitchEnum.Flights ? (
          <FlightsSearch
            allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies}
          />
        ) : currentSwitch === BookingSwitchEnum.Hotels ? (
          <HotelsSearch
            allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies}
          />
        ) : (
          <AutosSearch
            allowedTravelers={allowedTravelers}
            isLoadingAllowedTravelers={isLoadingAllowedTravelers}
            isLoadingBranchCompanies={isLoadingBranchCompanies}
            branchCompanies={branchCompanies}
          />
        )}
      </Body>

      <Menu show={isShow} onClose={closeBottomSheet} />
    </Screen>
  );
};

export default Booking;
